import { Box, Flex, Input } from "@chakra-ui/react";
import { Text } from "@chakra-ui/react";
import Card from "@root/components/enrollment-card/card";
import { useTranslation } from "react-i18next";
import { StyledSelect } from "@root/components/common/select/StyledSelect";
import { useNavigate } from "react-router-dom";
import { COURSE_REQUIREMENT } from "@root/constants";
import { ROUTE_NAMES } from "@root/constants/navigation-routes";
import CustomSelect from "../common/CustomSelect";
import { Paginate } from "@root/components/pagination";
import { RECORDS_PER_PAGE_COURSES } from "@root/constants";
import { Spinner } from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { DateField } from "@root/components";

interface MobileCoursesProps {
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;
  selectedRequirement: string;
  setSelectedRequirement: (requirement: string) => void;
  categoriesOptions: any[];
  filteredCourses: any[];
  getCoursesLoading: boolean;
  page: number;
  pageCount: number;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  dateFieldValue: string[] | undefined;
  handleDateChange: (date: any) => void;
  clearDates: () => void;
}

const MobileCourses = ({
  selectedCategories,
  setSelectedCategories,
  selectedRequirement,
  setSelectedRequirement,
  categoriesOptions,
  filteredCourses,
  getCoursesLoading,
  page,
  pageCount,
  setPage,
  setSearch,
  dateFieldValue,
  handleDateChange,
  clearDates,
}: MobileCoursesProps) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const requirementOptions = [
    {
      label: t("coursesPage.essentialRequired"),
      value: COURSE_REQUIREMENT.ESSENTIAL,
    },
    {
      label: t("coursesPage.optionalRecommended"),
      value: COURSE_REQUIREMENT.OPTIONAL,
    },
  ];

  const renderCourses = () => {
    return (
      <Box
        overflowY="auto"
        overflowX="hidden"
        maxH={"calc(100vh - 265px)"}
        css={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#a8a8a8",
          },
        }}
      >
        <Flex flexDirection="column" gap="12px" pr="4px">
          {filteredCourses?.map((course: any, index: number) => (
            <Box key={index} width="100%">
              <Card
                course={course}
                onClick={() =>
                  navigate(
                    `${ROUTE_NAMES.JOURNALIST_COURSE_DETAIL}/${course?.id}`,
                  )
                }
              />
            </Box>
          ))}
        </Flex>
      </Box>
    );
  };

  const renderContent = () => {
    if (getCoursesLoading)
      return (
        <Flex justify="center" align="center" minHeight="200px">
          <Spinner size="lg" />
        </Flex>
      );
    else
      return (
        <>
          {renderCourses()}

          {/* Pagination */}
          {pageCount > 0 && (
            <Box mt={1} display="flex" justifyContent="center">
              <Paginate
                marginTop={6}
                page={page}
                count={pageCount}
                pageSize={RECORDS_PER_PAGE_COURSES}
                onPageChange={(newPage: number) => {
                  setPage(newPage);
                }}
                variant="ghost"
              />
            </Box>
          )}
        </>
      );
  };

  return (
    <Box
      py={4}
      px={4}
      bg="#fafbfc"
      width="100%"
      maxW="100%"
      overflowX="hidden"
      height={"100% !important"}
      className="ali"
    >
      <Box width="100%" maxW="100%" mx="auto" height={"100% !important"}>
        <Text fontWeight={600} fontSize={"24px"} color={"#2F2F34"} mb={4}>
          {t("coursesPage.getStarted")}
        </Text>
        {/* <Text fontSize="16px" fontWeight={400} textColor={"#798692"} mb={6}>
          {t("coursesPage.browseAndEnroll")}
        </Text> */}

        {/* Mobile Layout */}
        <Flex flexDirection="column" gap="12px" mb={5}>
          {/* Search Bar */}
          <Box position="relative">
            <Input
              placeholder={t("coursesPage.searchPlaceholder")}
              borderRadius="12px"
              bg="white"
              border="1px solid #E6E9EF"
              pl="40px"
              pr="16px"
              py="12px"
              fontSize="14px"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                }
              }}
            />
            <SearchIcon
              position="absolute"
              left="12px"
              top="50%"
              transform="translateY(-50%)"
              color="#9CA3AF"
              boxSize="16px"
            />
          </Box>

          {/* Filter Buttons */}
          <Flex justifyContent={"end"} gap="12px" flexWrap="wrap">
            <StyledSelect
              placeholder={t("coursesPage.categories")}
              data={categoriesOptions}
              selectedValues={selectedCategories}
              onChange={setSelectedCategories}
              width="100%"
              height="44px"
              borderRadius="12px"
              border="1px solid #E6E9EF"
              bg="white"
              fontSize="14px"
              color="#9CA3AF"
            />

            <CustomSelect
              options={requirementOptions}
              value={selectedRequirement}
              onChange={setSelectedRequirement}
              placeholder={t("coursesPage.requirement")}
              height="44px"
              borderRadius="100px"
              border="1px solid #E6E9EF"
              bg="white"
              fontSize="14px"
              color="#9CA3AF"
            />

            <Box position={"relative"} height={"fit-content"} width={"100%"}>
              <DateField
                height="44px"
                py={0}
                placeholder={t("translate.enrollmentPeriod")}
                value={dateFieldValue}
                onChange={handleDateChange}
                onClear={clearDates}
                disabled={false}
                isISO
                range
                inputWidth="100%"
              />
            </Box>
          </Flex>
        </Flex>

        {/* Render Content */}
        {renderContent()}
      </Box>
    </Box>
  );
};

export default MobileCourses;
